import styles from "./StockOptionsRow.module.scss";
import { FC, useContext, useEffect, useRef, useState } from "react";
import { nFormatter } from "@/lib/utils";
import { onParsedMessage } from "@/lib/wsDispatch";
import { Context } from "@/lib/Context";
import { useAtomValue } from "jotai";
import { darkerBackgroundColorState, lighterBackgroundColorState } from "@/lib/store";
import { ChainData } from "@/models/stock-options";
import { getImpliedVolatility } from "implied-volatility";
import dayjs from "dayjs";
import { Box, Chip } from "@mui/material";

/** What a trade chip hands back: enough to identify one contract, so the caller can resolve
 *  it to an OCC symbol. `optionType` is essential — without it the caller cannot tell a call
 *  from a put and can only guess. */
export interface OptionChainSelection {
    strike: number;
    /** "YYMMDD", as the websocket chain reports expiries. */
    expiryDate: string;
    optionType: "call" | "put";
}

interface PropsTypes {
    breakEvenPrices: boolean;
    data: [string, { C: ChainData; P: ChainData }];
    expiryDate: string;
    callPrice: number;
    putPrice: number;
    price: number;
    trading?: boolean;
    handleOpen?: (open: boolean, row: OptionChainSelection, tradeAction: "Buy" | "Sell") => void;
}

const StockOptionsRow: FC<PropsTypes> = ({ breakEvenPrices, data, expiryDate, callPrice, putPrice, price, trading, handleOpen }) => {
    const { ortexWebSocket } = useContext(Context);
    const darkerBackgroundColor = useAtomValue(darkerBackgroundColorState);
    const lighterBackgroundColor = useAtomValue(lighterBackgroundColorState);

    const [row, setRow] = useState(data[1]);

    const currentTime = useRef(dayjs.utc());

    const strike = Number(data[0]);

    const getImpVolatilities = () => {
        const yearDiff =
            (currentTime.current.hour(14).minute(30).isAfter(currentTime.current)
                ? Math.ceil(dayjs.utc(expiryDate, "YYMMDD").hour(14).minute(30).diff(currentTime.current, "days", true))
                : dayjs.utc(expiryDate, "YYMMDD").hour(14).minute(30).diff(currentTime.current, "days", true) + 1) / 365;

        const callIV = nFormatter(getImpliedVolatility(row.C.LP, callPrice, strike, yearDiff, 0.0012, "call") * 100, 2);
        const putIV = nFormatter(getImpliedVolatility(row.P.LP, putPrice, strike, yearDiff, 0.0012, "put") * 100, 2);

        return { callIV, putIV };
    };

    const [{ callIV, putIV }, setImpVolatilities] = useState({ callIV: "", putIV: "" });

    // One listener per socket for the whole chain (wsDispatch): each strike used to parse every frame itself.
    useEffect(() => {
        if (!ortexWebSocket) return;
        return onParsedMessage(ortexWebSocket, ({ OT }) => {
            if (OT && OT.ST === strike) setRow((row) => ({ ...row, [OT.OT]: { LP: OT.P, OI: OT.OI, V: OT.V } }));
        });
    }, [ortexWebSocket, strike]);

    useEffect(() => {
        setImpVolatilities(getImpVolatilities());
    }, [row]);

    const isStrikeLower = strike <= price;
    const isStrikeHigher = strike > price;

    // Both actions on both sides. Previously the Calls column offered only Buy and the Puts
    // column only Sell, which made three of the four supported strategies unreachable: a
    // covered call is a SELL of a call, a cash-secured put is a SELL of a put, and a long put
    // is a BUY of a put. Only the long call was expressible.
    const tradeChips = (optionType: "call" | "put") => (
        <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center" }}>
            {(["Buy", "Sell"] as const).map((action) => (
                <Chip
                    key={action}
                    variant="outlined"
                    label={action}
                    color={action === "Buy" ? "primary" : "error"}
                    size="small"
                    style={{ width: "fit-content", height: "75%", minHeight: "75%", fontWeight: "bold", borderWidth: 2, margin: "auto", zIndex: 0 }}
                    sx={{ marginX: 0 }}
                    onClick={() => handleOpen?.(true, { strike, expiryDate, optionType }, action)}
                />
            ))}
        </Box>
    );

    const shouldShowValue = (impVolatility: number, CallOrPut: { V: number; OI: number; LP: number }) => {
        return !(impVolatility > 10000 || CallOrPut.V === 0 || CallOrPut.OI === 0 || CallOrPut.LP === 0);
    };

    return (
        <>
            <style jsx>{`
                td div {
                    border-color: ${darkerBackgroundColor};
                }
                .highlighted {
                    background-color: ${lighterBackgroundColor};
                }
            `}</style>
            <tr className={styles.StockOptionsRow}>
                {trading && (
                    <td>
                        <div className={isStrikeLower && "highlighted"}>{tradeChips("call")}</div>
                    </td>
                )}
                <td>
                    <div className={isStrikeLower && "highlighted"}>
                        <p>{parseFloat(callIV) && shouldShowValue(parseFloat(callIV), row.C) ? `${callIV} %` : "N/A"}</p>
                    </div>
                </td>
                <td>
                    <div className={isStrikeLower && "highlighted"}>
                        <p>{nFormatter(row.C.OI, 2)}</p>
                    </div>
                </td>
                <td>
                    <div className={isStrikeLower && "highlighted"}>
                        <p>{nFormatter(row.C.V, 2)}</p>
                    </div>
                </td>
                <td>
                    <div className={isStrikeLower && "highlighted"}>
                        <p className={!isFinite(price) ? "" : isStrikeHigher ? "negative" : "positive"}>{nFormatter(row.C.LP, 2)}</p>
                    </div>
                </td>
                <td className={styles.strike}>
                    <div>
                        <p style={{ textAlign: "center" }}>{nFormatter(Number(data[0]), 2)}</p>
                        <p>
                            <span className="positive">{breakEvenPrices ? nFormatter(strike + row.C.LP, 2) : ""}</span>
                            <span className="negative">{breakEvenPrices ? nFormatter(strike - row.P.LP, 2) : ""}</span>
                        </p>
                    </div>
                </td>
                <td>
                    <div className={isStrikeHigher && "highlighted"}>
                        <p className={!isFinite(price) ? "" : isStrikeHigher ? "positive" : "negative"}>{nFormatter(row.P.LP, 2)}</p>
                    </div>
                </td>
                <td>
                    <div className={isStrikeHigher && "highlighted"}>
                        <p>{nFormatter(row.P.V, 2)}</p>
                    </div>
                </td>
                <td>
                    <div className={isStrikeHigher && "highlighted"}>
                        <p>{nFormatter(row.P.OI, 2)}</p>
                    </div>
                </td>
                <td>
                    <div className={isStrikeHigher && "highlighted"}>
                        <p>{parseFloat(putIV) && shouldShowValue(parseFloat(putIV), row.P) ? `${putIV} %` : "N/A"}</p>
                    </div>
                </td>
                {trading && (
                    <td>
                        <div className={isStrikeHigher && "highlighted"}>{tradeChips("put")}</div>
                    </td>
                )}
            </tr>
        </>
    );
};

export default StockOptionsRow;
